import React, { useState, useEffect, useCallback } from 'react';
import { Typography } from 'antd';
import { ClockCircleOutlined } from '@ant-design/icons';
import { getUnifiedTasks, type UnifiedTaskRow } from '@/services/unifiedTask.service';
import { UnifiedTasksExecutionBoard } from '@/components/unified/UnifiedTasksExecutionBoard';
import { UnifiedTaskSavedViewsBar } from '@/components/unified/UnifiedTaskSavedViewsBar';
import { applySavedViewFilterRecord } from '@/utils/unifiedTaskSavedViewApply';

const { Title, Paragraph } = Typography;

const MyTasksPage: React.FC = () => {
  const [tasks, setTasks] = useState<UnifiedTaskRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [moduleFilter, setModuleFilter] = useState<string | undefined>(undefined);
  const [taskTypeFilter, setTaskTypeFilter] = useState<string | undefined>(undefined);
  const [sourceDocTypeFilter, setSourceDocTypeFilter] = useState<string | undefined>(undefined);
  const [taskOriginFilter, setTaskOriginFilter] = useState<'all' | 'system' | 'manual'>('all');
  const [dueTodayOnly, setDueTodayOnly] = useState(false);
  const [noDueDateOnly, setNoDueDateOnly] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [openTasksOnly, setOpenTasksOnly] = useState(true);
  const [taskSearchQ, setTaskSearchQ] = useState('');
  const [taskSearchDebounced, setTaskSearchDebounced] = useState('');
  const [priorityFilter, setPriorityFilter] = useState<string | undefined>(undefined);
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [blockedOnly, setBlockedOnly] = useState(false);
  const [readyToStart, setReadyToStart] = useState(false);
  const [slaRiskFilter, setSlaRiskFilter] = useState<
    'all' | 'safe' | 'warning' | 'breach' | 'critical' | 'none'
  >('all');
  const [sortBySla, setSortBySla] = useState(false);

  useEffect(() => {
    const id = setTimeout(() => setTaskSearchDebounced(taskSearchQ), 400);
    return () => clearTimeout(id);
  }, [taskSearchQ]);

  const fetchTasks = useCallback((opts?: { sync?: boolean }) => {
    setLoading(true);
    getUnifiedTasks({
      sync: opts?.sync === true,
      assigned_to: 'me',
      module: moduleFilter,
      q: taskSearchDebounced.trim() || undefined,
      priority: priorityFilter,
      overdue_only: overdueOnly || undefined,
      task_type: taskTypeFilter,
      source_document_type: sourceDocTypeFilter,
      origin: taskOriginFilter === 'all' ? undefined : taskOriginFilter,
      due_today_only: dueTodayOnly || undefined,
      no_due_date_only: noDueDateOnly || undefined,
      status: statusFilter,
      open_only: openTasksOnly && !statusFilter ? true : undefined,
      sla_risk: slaRiskFilter !== 'all' ? slaRiskFilter : undefined,
      blocked_only: blockedOnly || undefined,
      ready_to_start: readyToStart || undefined,
      sort: sortBySla ? 'sla' : undefined,
      limit: 500,
      offset: 0,
    })
      .then(setTasks)
      .catch(() => setTasks([]))
      .finally(() => setLoading(false));
  }, [
    moduleFilter,
    taskSearchDebounced,
    priorityFilter,
    overdueOnly,
    taskTypeFilter,
    sourceDocTypeFilter,
    taskOriginFilter,
    dueTodayOnly,
    noDueDateOnly,
    statusFilter,
    openTasksOnly,
    blockedOnly,
    readyToStart,
    slaRiskFilter,
    sortBySla,
  ]);

  const buildMyTasksFiltersSnapshot = useCallback((): Record<string, unknown> => {
    const filters: Record<string, unknown> = { me: true };
    if (moduleFilter) filters.module = moduleFilter;
    if (statusFilter) filters.status = statusFilter;
    if (priorityFilter) filters.priority = priorityFilter;
    if (taskTypeFilter) filters.task_type = taskTypeFilter;
    if (sourceDocTypeFilter) filters.sdoc = sourceDocTypeFilter;
    if (taskOriginFilter !== 'all') filters.origin = taskOriginFilter;
    const tq = taskSearchDebounced.trim();
    if (tq) filters.tq = tq.slice(0, 200);
    if (overdueOnly) filters.overdue = true;
    if (dueTodayOnly) filters.due_today = true;
    if (noDueDateOnly) filters.nodue = true;
    if (openTasksOnly) filters.open = true;
    if (blockedOnly) filters.blocked = true;
    if (readyToStart) filters.ready = true;
    if (slaRiskFilter !== 'all') filters.sla = slaRiskFilter;
    if (sortBySla) filters.sla_sort = true;
    return filters;
  }, [
    moduleFilter,
    statusFilter,
    priorityFilter,
    taskTypeFilter,
    sourceDocTypeFilter,
    taskOriginFilter,
    taskSearchDebounced,
    overdueOnly,
    dueTodayOnly,
    noDueDateOnly,
    openTasksOnly,
    blockedOnly,
    readyToStart,
    slaRiskFilter,
    sortBySla,
  ]);

  const applyMyTasksFilters = useCallback((f: Record<string, unknown>) => {
    applySavedViewFilterRecord(f, {
      setTaskModuleFilter: setModuleFilter,
      setStatusFilter: setStatusFilter,
      setPriorityFilter: setPriorityFilter,
      setTaskTypeFilter: setTaskTypeFilter,
      setSourceDocTypeFilter: setSourceDocTypeFilter,
      setTaskOriginFilter: setTaskOriginFilter,
      setTaskSearchQ: setTaskSearchQ,
      setTaskSearchDebounced: setTaskSearchDebounced,
      setOverdueOnly: setOverdueOnly,
      setDueTodayOnly: setDueTodayOnly,
      setNoDueDateOnly: setNoDueDateOnly,
      setOpenTasksOnly: setOpenTasksOnly,
      setBlockedOnly: setBlockedOnly,
      setReadyToStart: setReadyToStart,
      setSlaRiskFilter: setSlaRiskFilter,
      setSortBySla: setSortBySla,
    });
  }, []);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  return (
    <div>
      <Title level={2}>
        <ClockCircleOutlined /> My Tasks
      </Title>
      <Paragraph>
        Unified worklist: tasks and approvals assigned to you. Use <strong>Refresh</strong> to re-sync from dashboards and
        workflows. Saved filter views are per user (same list as the enterprise task board).
      </Paragraph>

      <UnifiedTasksExecutionBoard
        tasks={tasks}
        loading={loading}
        onRefresh={() => fetchTasks({ sync: true })}
        hideAssignedToMeToggle
        moduleFilter={moduleFilter}
        onModuleFilterChange={setModuleFilter}
        searchQuery={taskSearchQ}
        onSearchQueryChange={setTaskSearchQ}
        taskTypeFilter={taskTypeFilter}
        onTaskTypeFilterChange={setTaskTypeFilter}
        sourceDocumentTypeFilter={sourceDocTypeFilter}
        onSourceDocumentTypeFilterChange={setSourceDocTypeFilter}
        taskOriginFilter={taskOriginFilter}
        onTaskOriginFilterChange={setTaskOriginFilter}
        dueTodayOnly={dueTodayOnly}
        onDueTodayOnlyChange={setDueTodayOnly}
        noDueDateOnly={noDueDateOnly}
        onNoDueDateOnlyChange={setNoDueDateOnly}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        openTasksOnly={openTasksOnly}
        onOpenTasksOnlyChange={setOpenTasksOnly}
        priorityFilter={priorityFilter}
        onPriorityFilterChange={setPriorityFilter}
        overdueOnly={overdueOnly}
        onOverdueOnlyChange={setOverdueOnly}
        blockedOnly={blockedOnly}
        onBlockedOnlyChange={setBlockedOnly}
        readyToStart={readyToStart}
        onReadyToStartChange={setReadyToStart}
        slaRiskFilter={slaRiskFilter}
        onSlaRiskFilterChange={setSlaRiskFilter}
        sortBySla={sortBySla}
        onSortBySlaChange={setSortBySla}
        allowManualCreate
        titleExtra={
          <UnifiedTaskSavedViewsBar
            buildFiltersSnapshot={buildMyTasksFiltersSnapshot}
            applyFiltersSnapshot={applyMyTasksFilters}
          />
        }
      />
    </div>
  );
};

export default MyTasksPage;
